import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { MainLayout } from "@/layouts/MainLayout";
import { DataTable, FilterPanel, CallAgentButton } from "@/components/common";
import type { Column, FilterConfig } from "@/components/common";
import { ingressSourceLabel } from "@/utils/ingressSource";
import { productLeadsApi, utmOptionsApi } from "@/api";
import { productsApi } from "@/api/products.api";
import { CreateUtmLinkModal } from "@/components/CreateUtmLinkModal";
import { LinkIcon, PlusIcon, ExcelIcon } from "@/components/icons";
import {
  LoanExtraFieldsDrawer,
  isLoanLead,
  extractExtraFields,
  formatCurrency,
  formatFieldLabel,
} from "@/components/LoanExtraFieldsDrawer";
import { Eye, FileText } from "lucide-react";
import type { ProductLead } from "@/types/productLeads.types";
import type { UtmOptions } from "@/types/utmOptions.types";
import { exportToExcel, type ExportColumn } from "@/utils/exportToExcel";
import { fetchAllForExport, EXPORT_PAGE_SIZE } from "@/utils/fetchAllForExport";
import styles from "./Leads.module.css";

// Local-development convenience only: CRA statically replaces NODE_ENV, so
// these never activate in a production build.
const IS_LOCAL_DEV = process.env.NODE_ENV === "development";
const DEV_FALLBACK_AGENT_ID = "19c813b0-3584-4b29-a4ac-f5f395fce200";

const LEAD_STATUS_OPTIONS = [
  { label: "Clicked", value: "CLICKED" },
  { label: "Initiated", value: "INITIATED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Disbursed", value: "DISBURSED" },
  { label: "Activated", value: "ACTIVATED" },
  { label: "Dropoff", value: "DROPOFF" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Unmapped", value: "UNMAPPED" },
];

const SEARCH_TYPE_OPTIONS = [
  { label: "Mobile Number", value: "number" },
  { label: "Name", value: "name" },
  { label: "User ID", value: "id" },
  { label: "Email", value: "email" },
  { label: "Agent Name", value: "agent_name" },
  { label: "Tracking ID", value: "tracking_id" },
  { label: "Token", value: "token" },
  { label: "Sub", value: "sub" },
];

type LeadsSearchType =
  | "number"
  | "name"
  | "id"
  | "email"
  | "agent_name"
  | "tracking_id"
  | "token"
  | "sub";

// Build a display name from flattened extra_fields keys.
const buildName = (row: ProductLead): string => {
  // Prefer the backend-resolved customer_name (from users_), else the form
  // fields flattened out of extra_fields (portal leads before users_ resolve).
  const resolved = (row.customer_name ?? "") as string;
  if (resolved.trim()) return resolved.trim();
  const first = (row.firstName ?? row.fname ?? "") as string;
  const last = (row.lastName ?? row.lname ?? "") as string;
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || "-";
};

const getStaticColumns = (
  onViewLoanDetails: (row: ProductLead) => void
): Column<ProductLead>[] => [
  {
    key: "name",
    label: "Name",
    sortable: false,
    defaultVisible: true,
    render: (_, row) => {
      const name = buildName(row);
      if (isLoanLead(row)) {
        return (
          <span
            style={{
              cursor: "pointer",
              color: "#1d4ed8",
              fontWeight: 500,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onViewLoanDetails(row);
            }}
            title="Click to view loan details in side modal"
          >
            {name}
          </span>
        );
      }
      return name;
    },
  },
  {
    key: "mobile",
    label: "Mobile",
    sortable: false,
    defaultVisible: true,
    // backend resolves customer_mob_num (users_ or click); fall back to the
    // form value flattened from extra_fields.
    render: (_, row) =>
      String(row.customer_mob_num || row.mobile || row.mob_num || "-"),
  },
  {
    key: "email",
    label: "Email",
    sortable: false,
    defaultVisible: true,
    render: (_, row) => String(row.customer_email || row.email || "-"),
  },
  {
    key: "product_id",
    label: "Product Id",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "tracking_id",
    label: "Tracking Id",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "mis_status",
    label: "MIS Status",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      const v = String(value || "Pending");
      return (
        <span
          style={{
            color: v === "Pending" ? "#9ca3af" : "#111827",
            fontWeight: 500,
          }}
        >
          {v}
        </span>
      );
    },
  },
  {
    key: "loan_details",
    label: "Loan Details",
    sortable: false,
    defaultVisible: true,
    render: (_, row) => {
      if (!isLoanLead(row)) {
        return <span className={styles.emptyDash}>—</span>;
      }
      return (
        <button
          type="button"
          className={styles.viewLoanDetailsBtn}
          onClick={(e) => {
            e.stopPropagation();
            onViewLoanDetails(row);
          }}
          title="View dynamic loan extra details in side modal"
        >
          <FileText size={13} />
          <span>View Details</span>
        </button>
      );
    },
  },
  {
    key: "agent_name",
    label: "Sub Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.agent_mob_num ?? undefined}
      />
    ),
  },
  {
    key: "parent_agent_name",
    label: "Parent Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.parent_agent_mob_num ?? undefined}
      />
    ),
  },
  {
    key: "created_at",
    label: "Created At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "updated_at",
    label: "Updated At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "user_id",
    label: "User ID",
    sortable: true,
    defaultVisible: false,
    render: (_, row) => String(row.user_id ?? "-"),
  },
  {
    key: "source",
    label: "Source",
    sortable: true,
    defaultVisible: false,
    render: (value) => ingressSourceLabel(value),
  },
];

// Frontend-only column removals (API still returns these keys). Kept out of
// both static columns and the dynamic-column picker.
const HIDDEN_KEYS = new Set<string>([
  // common removals (clicks + leads)
  "id",
  "is_active",
  "evaluation_id",
  "token",
  "vendor_status",
  "description",
  "agent_mob_num",
  "agent_email",
  "parent_agent_mob_num",
  "source_product_id",
  // leads-only removals
  "lead_id",
  "status",
  "redirect_link",
  "product_code",
]);

const FILTER_CONFIGS_BASE: FilterConfig[] = [
  {
    key: "search_type",
    label: "Search Type",
    type: "select",
    options: SEARCH_TYPE_OPTIONS,
  },
  {
    key: "search_text",
    label: "Search",
    type: "text",
    placeholder: "Enter search text...",
  },
  {
    key: "status",
    label: "Lead Status",
    type: "multiSelect",
    options: LEAD_STATUS_OPTIONS,
  },
  {
    key: "date_range",
    label: "Date Range",
    type: "dateRangeField",
    dateRangeFields: [
      { label: "Created At", value: "created_at" },
      { label: "Updated At", value: "updated_at" },
    ],
  },
  {
    key: "product_id",
    label: "Product",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search products...",
  },
  {
    key: "campaign_id",
    label: "Campaign",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search campaign...",
  },
  {
    key: "affiliate_code",
    label: "Affiliate Code",
    type: "text",
    placeholder: "Partial match...",
  },
];

export const ProductLeads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [leads, setLeads] = useState<ProductLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLoanLead, setSelectedLoanLead] = useState<ProductLead | null>(null);

  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;

  // Campaign filter is admin-only (selfOnboard).
  const isSelfOnboard =
    (localStorage.getItem("agent_id") || localStorage.getItem("agentId")) ===
    "selfOnboard";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState<boolean | undefined>(undefined);

  const [exportLimit, setExportLimit] = useState<number>(10);
  const [exporting, setExporting] = useState(false);
  const [showUtmModal, setShowUtmModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyAffiliateLink = () => {
    const id =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId") ||
      "";
    navigator.clipboard.writeText(`https://login.clickpe.ai/?agentId=${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [filters, setFilters] = useState<Record<string, unknown>>(() => {
    const status = searchParams.getAll("status");
    const mis_status = searchParams.getAll("mis_status");
    const product_id = searchParams.getAll("product_id");
    const date_range = searchParams.getAll("date_range");
    return {
      status: status.length > 0 ? status : [],
      mis_status: mis_status.length > 0 ? mis_status : [],
      product_id: product_id.length > 0 ? product_id : [],
      date_range_field: searchParams.get("date_range_field") || null,
      date_range: date_range.length === 2 ? date_range : null,
      search_text: searchParams.get("search_text") || "",
      search_type: searchParams.get("search_type") || null,
      campaign_id: searchParams.getAll("campaign_id"),
      affiliate_code: searchParams.get("affiliate_code") || "",
    };
  });

  const [searchTextInput, setSearchTextInput] = useState(
    () => searchParams.get("search_text") || ""
  );

  const [productFilterOptions, setProductFilterOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const [utmOptions, setUtmOptions] = useState<UtmOptions>({
    utm_source: [],
    utm_medium: [],
    utm_term: [],
    utm_content: [],
    campaign_id: [],
    sub: [],
  });

  const FILTER_CONFIGS: FilterConfig[] = useMemo(
    () =>
      FILTER_CONFIGS_BASE.filter(
        (f) => f.key !== "campaign_id" || isSelfOnboard
      ).map((f) => {
        if (f.key === "product_id") {
          return { ...f, options: productFilterOptions };
        }
        if (f.key === "campaign_id") {
          const vals = utmOptions.campaign_id;
          return { ...f, options: vals.map((v) => ({ label: v, value: v })) };
        }
        return f;
      }),
    [productFilterOptions, utmOptions, isSelfOnboard],
  );

  useEffect(() => {
    if (!isAuthenticated && !IS_LOCAL_DEV) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchProductOptions = async () => {
      try {
        const result = await productsApi.getProducts({ channel: "AGENT" });
        if (result.status === "Success" && Array.isArray(result.response)) {
          setProductFilterOptions(
            result.response.map((p) => ({
              label: `${p.name} (${p.code || p.id})`,
              value: p.id,
            })),
          );
        }
      } catch {
        // silently keep empty options
      }
    };
    fetchProductOptions();
  }, []);

  useEffect(() => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");
    if (!agentId) return;
    utmOptionsApi
      .getUtmOptions({ agent_id: agentId, scope: "leads" })
      .then(setUtmOptions);
  }, [agentIdOverride]);

  const buildBaseRequest = useCallback(
    (agentId: string, currentPage: number, currentPageSize: number) => {
      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
              filters.date_range[0] as string | null,
              filters.date_range[1] as string | null,
            ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];

      const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
        ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
        : undefined;

      const commonFilters = {
        status:
          Array.isArray(filters.status) && filters.status.length > 0
            ? (filters.status as string[])
            : undefined,
        mis_status:
          Array.isArray(filters.mis_status) && filters.mis_status.length > 0
            ? (filters.mis_status as string[])
            : undefined,
        product_id:
          Array.isArray(filters.product_id) && filters.product_id.length > 0
            ? (filters.product_id as string[])
            : undefined,
        campaign_id:
          Array.isArray(filters.campaign_id) && filters.campaign_id.length > 0
            ? (filters.campaign_id as string[])
            : undefined,
      };

      const affiliateCode = String(filters.affiliate_code || "").trim();
      const likeFilters = affiliateCode
        ? { affiliate_code: affiliateCode }
        : undefined;

      return {
        agent_id: agentId,
        page: currentPage,
        page_size: currentPageSize,
        ranges: {
          created_at: createdAtRange,
          updated_at: updatedAtRange,
        },
        sort_by: sortByValue,
        filters: commonFilters,
        like_filters: likeFilters,
      };
    },
    [filters, sortBy, sortOrder]
  );

  const fetchLeads = useCallback(async () => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId") ||
      // Dev-only fallback so the page can be previewed without agent login.
      (IS_LOCAL_DEV ? DEV_FALLBACK_AGENT_ID : null);

    if (!agentId) {
      setError("Agent ID not found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;

      const searchTypeValue: LeadsSearchType | undefined = searchType
        ? (searchType as LeadsSearchType)
        : undefined;

      const request = {
        ...buildBaseRequest(agentId, page, pageSize),
        search_text: searchText || undefined,
        search_type: searchTypeValue,
      };

      const response = await productLeadsApi.getProductLeads(request);

      if (response.response && response.response.length > 0) {
        setLeads(response.response);
        setHasMore(response.pagination_metadata?.has_more);
      } else {
        setLeads([]);
        setHasMore(false);
      }
    } catch {
      setLeads([]);
      setHasMore(false);
      setError("Failed to fetch leads. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, buildBaseRequest, filters, agentIdOverride]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleSort = (key: string, order: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === "search_text") {
      const text = typeof value === "string" ? value : "";
      setSearchTextInput(text);
      if (!text) {
        setFilters((prev) => ({ ...prev, search_text: "" }));
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("search_text");
          return newParams;
        });
      }
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((v) => newParams.append(key, String(v)));
      } else if (value !== null && value !== undefined && value !== "") {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  const handleSearchSubmit = (_key: string, value: unknown) => {
    const text = typeof value === "string" ? value : "";
    setFilters((prev) => ({
      ...prev,
      search_text: text,
    }));
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (text) {
        newParams.set("search_text", text);
      } else {
        newParams.delete("search_text");
      }
      return newParams;
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchTextInput("");
    setFilters({
      status: [],
      mis_status: [],
      product_id: [],
      date_range_field: null,
      date_range: null,
      search_text: "",
      search_type: null,
      campaign_id: [],
      affiliate_code: "",
    });
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      [
        "status",
        "mis_status",
        "date_range_field",
        "date_range",
        "search_text",
        "search_type",
        "product_id",
        "campaign_id",
        "affiliate_code",
      ].forEach((k) => newParams.delete(k));
      return newParams;
    });
  };

  const handleExportToExcel = async () => {
    const tableId = "product_leads";
    const savedConfigStr = localStorage.getItem(`table_config_${tableId}`);
    // Copy so a later sort() can never mutate the memoized allColumns array.
    let targetColumns = [...allColumns];

    if (savedConfigStr) {
      try {
        const config = JSON.parse(savedConfigStr);
        if (config.visibleColumns && Array.isArray(config.visibleColumns)) {
          const visibleSet = new Set(config.visibleColumns);
          targetColumns = allColumns.filter((col) => visibleSet.has(col.key));

          if (config.columnOrder && Array.isArray(config.columnOrder)) {
            targetColumns.sort((a, b) => {
              const indexA = config.columnOrder.indexOf(a.key);
              const indexB = config.columnOrder.indexOf(b.key);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          }
        } else {
          // Config parsed but has no valid visibleColumns list — use defaults.
          targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
        }
      } catch (e) {
        console.error(
          "Error parsing saved config for export, using default.",
          e
        );
        targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
      }
    } else {
      targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
    }

    setExporting(true);
    setError("");

    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId") ||
        // Dev-only fallback, same as fetchLeads, so export works locally.
        (IS_LOCAL_DEV ? DEV_FALLBACK_AGENT_ID : null);

      if (!agentId) throw new Error("Agent ID not found");

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const searchTypeValue: LeadsSearchType | undefined = searchType
        ? (searchType as LeadsSearchType)
        : undefined;

      const dataToExport = await fetchAllForExport<ProductLead>(
        async (page, pageSize) => {
          const res = await productLeadsApi.getProductLeads({
            ...buildBaseRequest(agentId, page, pageSize),
            search_text: searchText || undefined,
            search_type: searchTypeValue,
          });
          if (!res.response) {
            throw new Error(
              res.message || "Failed to fetch product leads data for export"
            );
          }
          return {
            rows: res.response,
            hasMore: res.pagination_metadata?.has_more,
          };
        },
        exportLimit,
        EXPORT_PAGE_SIZE
      );

      if (dataToExport.length === 0) {
        alert("No data found to export.");
        return;
      }

      // --- Full extra-fields export (mirrors the LoanExtraFieldsDrawer) ---
      // Pre-compute extra_fields per row once (avoids re-parsing per cell).
      const extrasByRow = new Map<ProductLead, Record<string, any>>();
      dataToExport.forEach((row) => {
        extrasByRow.set(row, extractExtraFields(row));
      });

      // Extra-field keys already covered by static columns (avoid duplicates).
      const coveredExtraKeys = new Set([
        "firstName",
        "lastName",
        "fname",
        "lname", // covered by "Name"
        "mobile",
        "mob_num", // covered by "Mobile"
        "email", // covered by "Email"
      ]);
      const targetColumnKeys = new Set(targetColumns.map((col) => col.key));

      // Union of all extra-field keys across the exported rows (sorted for a
      // stable column order), skipping keys already exported elsewhere.
      const extraKeys = Array.from(
        new Set(
          dataToExport.flatMap((row) =>
            Object.keys(extrasByRow.get(row) ?? {})
          )
        )
      )
        .filter(
          (key) => !coveredExtraKeys.has(key) && !targetColumnKeys.has(key)
        )
        .sort();

      const isAmountLikeKey = (key: string) => {
        const lk = key.toLowerCase();
        return (
          lk.includes("amount") ||
          lk.includes("income") ||
          lk.includes("salary") ||
          lk.includes("emi") ||
          lk.includes("fee") ||
          lk.includes("cost") ||
          lk.includes("turnover")
        );
      };

      // Format an extra-field value the same way the drawer renders it.
      const renderExtraCell = (key: string, val: unknown): string => {
        if (val === null || val === undefined || val === "") return "-";
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (Array.isArray(val)) {
          return val
            .map((item) =>
              typeof item === "object" ? JSON.stringify(item) : String(item)
            )
            .join(", ");
        }
        if (typeof val === "object") return JSON.stringify(val);
        if (isAmountLikeKey(key) && !isNaN(Number(val))) {
          return formatCurrency(val);
        }
        return String(val);
      };

      const extraExportColumns: ExportColumn<ProductLead>[] = extraKeys.map(
        (key) => ({
          key,
          label: formatFieldLabel(key),
          render: (_value: unknown, row: ProductLead) =>
            renderExtraCell(key, (extrasByRow.get(row) ?? {})[key]),
        })
      );

      // "loan_details" is a UI action column; its old text summary is now
      // fully represented by the individual extra-field columns above.
      const exportColumns: ExportColumn<ProductLead>[] = [
        ...targetColumns
          .filter((col) => col.key !== "loan_details")
          .map((col) => {
            if (!col.render) return { key: col.key, label: col.label };
            return {
              key: col.key,
              label: col.label,
              render: (value: unknown, row: ProductLead) => {
                const rawValue = (row as unknown as Record<string, unknown>)[
                  col.key
                ];
                if (col.key === "name") return buildName(row);
                if (col.key === "mobile")
                  return String(
                    row.customer_mob_num || row.mobile || row.mob_num || "-"
                  );
                if (col.key === "email")
                  return String(row.customer_email || row.email || "-");
                if (col.key === "mis_status")
                  return String(rawValue || "Pending");
                if (
                  col.key.includes("_date") ||
                  col.key.includes("created_at") ||
                  col.key.includes("updated_at")
                ) {
                  if (!rawValue) return "-";
                  try {
                    return new Date(rawValue as string).toLocaleString();
                  } catch {
                    return String(rawValue);
                  }
                }
                if (rawValue === null || rawValue === undefined) return "-";
                return String(rawValue);
              },
            };
          }),
        ...extraExportColumns,
      ];

      exportToExcel(
        dataToExport as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        "product_leads"
      );
    } catch (err) {
      console.error(err);
      const errorMsg =
        err instanceof Error ? err.message : "Failed to export data";
      setError(errorMsg);
      alert(`Export Failed: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const staticColumns = useMemo(
    () => getStaticColumns((lead) => setSelectedLoanLead(lead)),
    []
  );

  const dynamicColumns = useMemo(() => {
    if (leads.length === 0) return [];
    // Union of keys across the page so per-product extra_fields all surface.
    const allKeys = new Set<string>();
    leads.forEach((lead) => {
      Object.keys(lead || {}).forEach((k) => allKeys.add(k));
    });
    const definedKeys = new Set(staticColumns.map((col) => col.key));
    // These are consumed by static columns and the loan side drawer; avoid duplicating them as raw columns.
    [
      "firstName",
      "lastName",
      "fname",
      "lname",
      "mob_num",
      "extra_fields",
      "loanAmount",
      "loanTenure",
      "income",
      "employment",
      "dob",
      "vendor",
    ].forEach((k) => definedKeys.add(k));
    const remainingKeys = Array.from(allKeys).filter(
      (key) => !definedKeys.has(key) && !HIDDEN_KEYS.has(key)
    );
    return remainingKeys.map((key) => {
      const label = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      let render:
        | ((value: unknown, row: ProductLead) => React.ReactNode)
        | undefined;
      if (
        key.includes("_amt") ||
        key.includes("amount") ||
        key.includes("income") ||
        key.includes("Amount") ||
        key.includes("Income")
      )
        render = (value) =>
          value ? `₹${Number(value).toLocaleString()}` : "-";
      else if (
        key.includes("_date") ||
        key.includes("created_at") ||
        key.includes("updated_at") ||
        key === "dob"
      )
        render = (value) => {
          try {
            return value ? new Date(value as string).toLocaleDateString() : "-";
          } catch {
            return String(value);
          }
        };
      else
        render = (value) => {
          if (value === null || value === undefined) return "-";
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        };
      return {
        key,
        label,
        sortable: false,
        defaultVisible: false,
        render,
      } as Column<ProductLead>;
    });
  }, [leads, staticColumns]);

  const allColumns = useMemo(
    () => [...staticColumns, ...dynamicColumns],
    [staticColumns, dynamicColumns]
  );

  if (!isAuthenticated && !IS_LOCAL_DEV) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Product Leads
              {agentName && (
                <span className={styles.agentContext}>({agentName})</span>
              )}
            </h1>
            {agentName && (
              <p className={styles.agentContextInfo}>
                Viewing product leads for: {agentName} (ID: {agentIdOverride})
              </p>
            )}
          </div>

          <div className={styles.headerControls}>
            <button
              className={`${styles.affiliateLinkButton} ${
                copied ? styles.copied : ""
              }`}
              onClick={handleCopyAffiliateLink}
              title="Copy your affiliate link to clipboard"
            >
              <LinkIcon size={18} />
              {copied ? "Copied!" : "Copy Affiliate Link"}
            </button>
            <button
              className={styles.primaryToolbarButton}
              onClick={() => setShowUtmModal(true)}
              title="Create a distributable UTM link"
            >
              <PlusIcon size={18} />
              Create UTM Link
            </button>
            <div className={styles.exportToolbar}>
              <select
                value={exportLimit}
                onChange={(e) => setExportLimit(Number(e.target.value))}
                className={styles.limitSelect}
                title="Select number of records to export"
              >
                <option value={10}>Export 10</option>
                <option value={50}>Export 50</option>
                <option value={100}>Export 100</option>
                <option value={500}>Export 500</option>
                <option value={1000}>Export 1000</option>
                <option value={5000}>Export 5000</option>
              </select>
              <button
                className={styles.exportButton}
                onClick={() => handleExportToExcel()}
                disabled={loading || exporting || leads.length === 0}
                title="Export to Excel"
              >
                {exporting ? (
                  "Exporting..."
                ) : (
                  <>
                    <ExcelIcon size={18} />
                    Export to Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <FilterPanel
          filters={FILTER_CONFIGS}
          values={{
            ...filters,
            search_text: searchTextInput,
          }}
          onChange={handleFilterChange}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleResetFilters}
        />
        <DataTable
          data={leads}
          columns={allColumns}
          loading={loading}
          tableId="product_leads"
          pagination={{
            page,
            pageSize,
            hasMore,
            onPageChange: setPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
          }}
          sorting={{ sortBy, sortOrder, onSort: handleSort }}
          onRowActions={[
            {
              label: "Loan Details",
              icon: <Eye size={15} />,
              variant: "secondary",
              onClick: (row) => setSelectedLoanLead(row),
              disabled: (row) => !isLoanLead(row),
            },
            {
              label: "Resume Journey",
              variant: "primary",
              // ponytail: link lives in redirect_link, older rows use description
              onClick: (row) => {
                const link = (row.redirect_link || row.description) as
                  | string
                  | undefined;
                if (link) {
                  const w = window.open(link, "_blank");
                  if (w) w.opener = null;
                }
              },
            },
          ]}
          emptyMessage="No product leads found"
        />
      </div>
      {showUtmModal && (
        <CreateUtmLinkModal
          agentId={
            agentIdOverride ||
            localStorage.getItem("agent_id") ||
            localStorage.getItem("agentId") ||
            ""
          }
          onClose={() => setShowUtmModal(false)}
        />
      )}
      <LoanExtraFieldsDrawer
        lead={selectedLoanLead}
        isOpen={!!selectedLoanLead}
        onClose={() => setSelectedLoanLead(null)}
      />
    </MainLayout>
  );
};
